import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaSortDown, FaSortUp, FaSync } from "react-icons/fa";

import Breadcrumbs from "../../../components/ui/Breadcrumbs.jsx";
import Table from "../../../components/backoffice/TemplateBroker/Table";
import { getList } from "../../../components/backoffice/TemplatePMS/templatePmsUtils.js";
import { errorNotify, successNotify } from "../../../Services/Notification.js";
import { sortData } from "../../../utils/sortData";

const syncApiUrl = `${process.env.REACT_APP_API_URL}api/manual-master-refresh/sync_master_to_instance/`;
const getMasterTablesApiUrl = `${process.env.REACT_APP_API_URL}api/manual-master-refresh/get_list/`;

const syncTargets = ["Dev", "Test", "Prod"];

const getHeaders = () => {
  const token = sessionStorage.getItem("subscriberToken")
    ? sessionStorage.getItem("subscriberToken")
    : sessionStorage.getItem("token");

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

const getTableName = (item) =>
  typeof item === "string"
    ? item
    : item?.refresh_table_name ?? item?.table_name ?? item?.name;

const SyncMaster = () => {
  const [masterTables, setMasterTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTableName, setRefreshTableName] = useState("");
  const [syncClicked, setSyncClicked] = useState([]);
  const [tableFilter, setTableFilter] = useState([]);
  const [sortingType, setSortingType] = useState("asc");
  const [sortColumn, setSortColumn] = useState("table_name");

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const res = await axios.post(getMasterTablesApiUrl, {}, {
          headers: getHeaders(),
        });
        setMasterTables(getList(res));
      } catch (err) {
        errorNotify(
          err?.response?.data?.message || "Unable to fetch master tables.",
        );
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const tableData = useMemo(
    () =>
      masterTables
        .map((item) => ({
          ...(typeof item === "object" ? item : {}),
          table_name: getTableName(item),
        }))
        .filter((row) => row.table_name),
    [masterTables],
  );

  useEffect(() => {
    setTableFilter(tableData);
  }, [tableData]);

  const handleSync = async (syncDataTo) => {
    if (!refreshTableName || syncClicked.includes(syncDataTo)) return;

    setSyncClicked((prev) => [...prev, syncDataTo]);
    try {
      const res = await axios.post(
        syncApiUrl,
        { refresh_table_name: refreshTableName, sync_data_to: syncDataTo },
        { headers: getHeaders() },
      );

      if (!res.data?.error) {
        successNotify(
          res.data?.message || `${refreshTableName} synced to ${syncDataTo}.`,
        );
      } else {
        errorNotify(res.data?.message || `Unable to sync to ${syncDataTo}.`);
      }
    } catch (err) {
      errorNotify(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setSyncClicked((prev) => prev.filter((item) => item !== syncDataTo));
    }
  };

  const handleSorting = (column, sortType, type) => {
    setSortColumn(column);
    setTableFilter(sortData({ data: tableData, column, type, sortType }));
    setSortingType(sortingType === "asc" ? "desc" : "asc");
  };

  const sortableHeader = (label, column, type) => (
    <div
      onClick={() =>
        handleSorting(column, sortingType === "asc" ? "desc" : "asc", type)
      }
      className="flex cursor-pointer justify-between"
    >
      <div>{label}</div>
      <div className="space-y-[-10px]">
        <FaSortUp
          className={
            sortColumn === column && sortingType === "asc"
              ? "opacity-100"
              : "opacity-50"
          }
        />
        <FaSortDown
          className={
            sortColumn === column && sortingType === "desc"
              ? "opacity-100"
              : "opacity-50"
          }
        />
      </div>
    </div>
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "sl",
        header: () => <div className=" text-center">SR. NO.</div>,
        cell: (info) => (
          <div className="text-center ">{info.row.index + 1}.</div>
        ),
      },
      {
        accessorKey: "table_name",
        header: () => sortableHeader("Master", "table_name", "string"),
        cell: (info) => <div className=" w-[800px]">{info.getValue()}</div>,
      },
    ],
    [sortColumn, sortingType, tableData],
  );

  return (
    <div className="app-container min-h-[65vh] pb-[60px] pt-[100px]">
      <Breadcrumbs items={["Home", "Back Office", "Sync Master"]} />
      <div className="mt-6 flex flex-wrap items-end gap-6">
        <div className="flex w-[220px] flex-col gap-2 text-neutral-600">
          <label className="text-[14px] font-[400] text-[#868686]">
            Master
          </label>
          <select
            value={refreshTableName}
            onChange={(event) => setRefreshTableName(event.target.value)}
            className="rounded bg-neutral-100 p-2 text-[#636363] focus:outline-none"
          >
            <option value="" disabled>
              Select
            </option>
            {tableData.map((row) => (
              <option key={row.table_name} value={row.table_name}>
                {row.table_name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          {syncTargets.map((syncType) => {
            const isSyncing = syncClicked.includes(syncType);
            const isDisabled = !refreshTableName || isSyncing;
            return (
              <button
                key={syncType}
                type="button"
                onClick={() => handleSync(syncType)}
                disabled={isDisabled}
                className={`${
                  isDisabled ? "cursor-not-allowed opacity-[50%]" : ""
                } flex min-w-[120px] items-center justify-center gap-2 rounded border-[1px] border-[#636363] px-2 py-1 font-bold text-[#636363]`}
              >
                <FaSync className={isSyncing ? "animate-spin" : ""} />
                {syncType.toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        <Table
          columns={columns}
          tableData={tableFilter}
          loading={loading}
          showSyncButtons={false}
        />
      </div>
    </div>
  );
};

export default SyncMaster;
